import { z } from 'zod'

import type { RefundChoice, RefundMode } from '@/lib/api/sales-return-api'
import { m } from '@/lib/paraglide/messages'

// M-44 — lựa chọn hoàn tiền dùng chung cho trả hàng, xác nhận hoàn và huỷ đơn.

export interface RefundFormValue {
    mode: RefundMode
    /** Chuỗi người dùng gõ; chỉ dùng khi mode = partial. */
    amount: string
    payment_method: '' | 'cash' | 'bank_transfer'
}

export function emptyRefund(): RefundFormValue {
    return { mode: 'none', amount: '', payment_method: '' }
}

/** Schema của khối hoàn tiền; `maxRefundable` = số khách trả thừa tối đa. */
export function createRefundSchema(maxRefundable: number) {
    return z
        .object({
            mode: z.enum(['none', 'full', 'partial']),
            amount: z.string(),
            payment_method: z.enum(['', 'cash', 'bank_transfer']),
        })
        .superRefine((v, ctx) => {
            if (v.mode === 'none') return
            if (v.payment_method === '') {
                ctx.addIssue({
                    code: 'custom',
                    path: ['payment_method'],
                    message: m.refund_err_method_required(),
                })
            }
            if (v.mode !== 'partial') return
            const amount = Number(v.amount)
            if (
                v.amount.trim() === '' ||
                !Number.isFinite(amount) ||
                amount <= 0
            ) {
                ctx.addIssue({
                    code: 'custom',
                    path: ['amount'],
                    message: m.refund_err_amount_required(),
                })
            } else if (amount > maxRefundable) {
                ctx.addIssue({
                    code: 'custom',
                    path: ['amount'],
                    message: m.refund_err_amount_max({
                        max: maxRefundable.toLocaleString('vi-VN'),
                    }),
                })
            }
        })
}

export type RefundFieldErrors = Partial<
    Record<'amount' | 'payment_method', string>
>

/** Lỗi theo field của khối hoàn tiền — cho các popup không dùng RHF. */
export function refundFieldErrors(
    value: RefundFormValue,
    maxRefundable: number,
): RefundFieldErrors {
    const res = createRefundSchema(maxRefundable).safeParse(value)
    if (res.success) return {}
    const out: RefundFieldErrors = {}
    for (const issue of res.error.issues) {
        const key = issue.path[0]
        if ((key === 'amount' || key === 'payment_method') && !out[key]) {
            out[key] = issue.message
        }
    }
    return out
}

/** Payload gửi BE; `none` → undefined (không hoàn). */
export function toRefundChoice(
    value: RefundFormValue,
): RefundChoice | undefined {
    if (value.mode === 'none' || value.payment_method === '') return undefined
    return {
        mode: value.mode,
        amount: value.mode === 'partial' ? Number(value.amount) : 0,
        payment_method: value.payment_method,
    }
}
